// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { isValidHolesCount } from '@/utils/holesCount';

import { formatLocalDate } from './localDate';

export type RoundUpdateGameWeight = {
  gameDefinitionId: string;
  weightPercent: number;
};

export type RoundUpdateGameRuleSet = {
  gameDefinitionId: string;
  ruleSetId: string;
};

export type RoundUpdateCourse = {
  id: string;
  name: string;
  address: string;
  lat: number;
  lon: number;
};

export type RoundUpdateAiPlayer = {
  playerId: string;
  kind: 'pure' | 'ghost';
  sourcePlayerId?: string | null;
};

export type RoundUpdateMessagePayload = {
  type: 'round.update';
  roundId: string;
  gameDefinitionIds?: string[];
  gameWeights?: RoundUpdateGameWeight[];
  gameRuleSets?: RoundUpdateGameRuleSet[];
  course?: RoundUpdateCourse;
  holesCount?: number;
  playDate?: string;
  name?: string;
  aiEnabled?: boolean;
  aiRevealMode?: 'in_advance' | 'on_score';
  aiPlayers?: RoundUpdateAiPlayer[];
};

export const buildRoundUpdateMessage = (args: {
  roundId: string;
  gameDefinitionIds?: string[];
  gameWeights?: RoundUpdateGameWeight[];
  gameRuleSets?: RoundUpdateGameRuleSet[];
  course?: RoundUpdateCourse;
  holesCount?: number;
  playDate?: Date | null;
  name?: string | null;
  aiEnabled?: boolean;
  aiRevealMode?: 'in_advance' | 'on_score';
  aiPlayers?: RoundUpdateAiPlayer[];
}): RoundUpdateMessagePayload => {
  const payload: RoundUpdateMessagePayload = {
    type: 'round.update',
    roundId: args.roundId,
  };

  if (args.gameDefinitionIds) {
    payload.gameDefinitionIds = [...args.gameDefinitionIds];
  }

  if (args.gameWeights) {
    payload.gameWeights = args.gameWeights.map((weight) => ({ ...weight }));
  }

  if (args.gameRuleSets) {
    payload.gameRuleSets = args.gameRuleSets.map((ruleSet) => ({ ...ruleSet }));
  }

  if (args.playDate) {
    payload.playDate = formatLocalDate(args.playDate);
  }

  if (args.course) {
    payload.course = { ...args.course };
  }

  if (isValidHolesCount(args.holesCount)) {
    payload.holesCount = args.holesCount;
  }

  if (args.name != null) {
    payload.name = args.name;
  }

  if (args.aiEnabled !== undefined) {
    payload.aiEnabled = args.aiEnabled;
    payload.aiRevealMode = args.aiRevealMode ?? 'on_score';
    payload.aiPlayers = (args.aiPlayers ?? []).map((player) => ({ ...player }));
  }

  return payload;
};
